import React from "react";
import { GameRecord } from "../../api/services/gameRecords/type";
import { UserName } from "../customers/customerDetail/UserName";
import { GameTableName } from "../customers/customerDetail/GameTableName";
import { CustomerName } from "./CustomerName";

interface GameRecordTableProps {
  gameRecords: GameRecord[];
  isLoading?: boolean;
  showCustomerColumn?: boolean;
  hasActiveFilters?: boolean;
  selectedDate?: string;
  selectedDateFrom?: string;
  selectedDateTo?: string;
  selectedGames?: string[];
  selectedTables?: string[];
  currentPage?: number;
  itemsPerPage?: number;
}

export const GameRecordTable: React.FC<GameRecordTableProps> = ({
  gameRecords,
  isLoading = false,
  showCustomerColumn = false,
  hasActiveFilters = false,
  selectedDate,
  selectedDateFrom,
  selectedDateTo,
  selectedGames = [],
  selectedTables = [],
  currentPage = 1,
  itemsPerPage = 10,
}) => {
  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });

  const formatTime = (dateString: string) =>
    new Date(dateString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });

  const getDateFilterText = () => {
    if (selectedDate) {
      return `Date: ${formatDate(selectedDate)}`;
    } else if (selectedDateFrom && selectedDateTo) {
      return `Date Range: ${formatDate(selectedDateFrom)} - ${formatDate(selectedDateTo)}`;
    }
    return null;
  };

  if (isLoading) {
    return (
      <div className="bg-slate-800 rounded-lg overflow-visible">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-slate-400">Loading game records...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-800 rounded-lg">
      {/* Show active filters */}
      {hasActiveFilters && (
        <div className="px-6 py-3 border-b border-slate-700 bg-slate-750">
          <div className="text-sm text-slate-300 flex flex-wrap gap-4">
            <span className="text-slate-400">Active filters:</span>
            {getDateFilterText() && (
              <span className="text-purple-400">{getDateFilterText()}</span>
            )}
            {selectedGames.length > 0 && (
              <span className="text-blue-400">
                {selectedGames.length} game(s) selected
              </span>
            )}
            {selectedTables.length > 0 && (
              <span className="text-green-400">
                {selectedTables.length} table(s) selected
              </span>
            )}
          </div>
        </div>
      )}

      {/* Horizontal scroll container */}
      <div className="overflow-x-auto">
        <table
          className={`w-full ${showCustomerColumn ? "min-w-[1400px]" : "min-w-[1200px]"}`}
        >
          <thead className="bg-slate-700">
            <tr>
              <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider min-w-[60px]">
                S.N.
              </th>
              {showCustomerColumn && (
                <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider min-w-[120px]">
                  Customer
                </th>
              )}
              <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider min-w-[80px]">
                Shift
              </th>
              <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider min-w-[100px]">
                Date
              </th>
              <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider min-w-[80px]">
                Time
              </th>
              <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider min-w-[120px]">
                Game Table
              </th>
              <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider min-w-[100px]">
                Bet
              </th>
              <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider min-w-[80px]">
                Win
              </th>
              <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider min-w-[80px]">
                Loss
              </th>
              <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider min-w-[100px]">
                Manager
              </th>
              <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider min-w-[100px]">
                Dealer
              </th>
              <th className="px-4 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider min-w-[100px]">
                Watcher
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700">
            {gameRecords.map((record, index) => (
              <tr
                key={record.id}
                className="hover:bg-slate-700 transition-colors"
              >
                <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-300">
                  {(currentPage - 1) * itemsPerPage + index + 1}
                </td>
                {showCustomerColumn && (
                  <td className="px-4 py-4 whitespace-nowrap text-sm">
                    <CustomerName
                      customerId={record.customerId}
                      fallback="Unknown Customer"
                    />
                  </td>
                )}
                <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-300">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {record.shift}
                  </span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-300">
                  {formatDate(record.createdAt)}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-300">
                  {formatTime(record.createdAt)}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm">
                  <GameTableName
                    gameTableId={record.gameTableId}
                    fallback="Unknown Table"
                  />
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-left text-sm text-slate-300">
                  {record.betAmount}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-left text-sm text-green-400">
                  {record.winAmount}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-left text-sm text-red-400">
                  {record.lossAmount}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm">
                  <UserName
                    userId={record.managerId}
                    fallback="Unknown Manager"
                  />
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm">
                  <UserName
                    userId={record.dealerId}
                    fallback="Unknown Dealer"
                  />
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm">
                  <UserName
                    userId={record.watcherId}
                    fallback="Unknown Watcher"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {gameRecords.length === 0 && (
        <div className="text-center py-12">
          <div className="text-slate-400">
            {hasActiveFilters
              ? "No game records found with the selected filters"
              : "No game records found"}
          </div>
        </div>
      )}
    </div>
  );
};
